import { format, formatInTimeZone, utcToZonedTime } from "date-fns-tz";
import { parseISO } from "date-fns";
import type { Locale } from "date-fns";
import { enUS, es } from "date-fns/locale";
import parse from "date-fns/parse";
import startOfDay from "date-fns/startOfDay";

export const DEFAULT_FORMAT_DATE_TIME = "yyyy-MM-dd HH:mm:ss";
export const DEFAULT_TIME_ZONE = "Europe/Madrid";
export const asUTC = (date: Date, dateFormat?: string): string => {
  return format(utcToZonedTime(date, "UTC"), dateFormat || "dd/MM/yyyy", { timeZone: "UTC" });
};
export const asUTCTime = (date: Date, dateFormat?: string): string => {
  return format(utcToZonedTime(date, "UTC"), dateFormat || "dd/MM/yyyy hh:mm:ss", {
    timeZone: "UTC",
  });
};

export const convertUTCToEstablishmentTime = (
  date: string,
  timeZone: string = DEFAULT_TIME_ZONE,
  dateFormat: string = DEFAULT_FORMAT_DATE_TIME
): string => {
  try {
    return formatInTimeZone(date, timeZone, dateFormat);
  } catch (e) {
    console.log("Error converting date", e);
    return formatInTimeZone(date, DEFAULT_TIME_ZONE, dateFormat);
  }
};

export function currentDate() {
  return new Date();
}

export function dateToString(date: Date, formatStr = "yyyy-MM-dd") {
  return format(date, formatStr);
}

export function stringToDate(date?: string | null) {
  if (!date) return new Date();
  return parseISO(date);
}

// @ts-ignore
export function dateStringToProtoTimestamp(date: string): proto.google.protobuf.Timestamp {
  // @ts-ignore
  const timestamp = new proto.google.protobuf.Timestamp();
  timestamp.fromDate(new Date(date));
  return timestamp;
}

function currentDateAtStartOfDay() {
  return startOfDay(new Date());
}

function isDateValid(value: unknown): value is Date {
  return value instanceof Date && isFinite((value as unknown) as number);
}

export function parseDate(str: string, format = "dd-MM-yyyy"): Date {
  const parsed = parse(str, format, currentDateAtStartOfDay());
  if (!isDateValid(parsed)) {
    throw new Error(`Parsing date error ${str} has an invalid format`);
  }
  return parsed;
}

export function convertToTimeZone(date: Date, timeZone: string): Date {
  return utcToZonedTime(date, timeZone);
}

/**
 * The `date-fns` locale matching a `react-intl` locale tag. English is the default rather than
 * Spanish: a tag this build has not been taught about is not Spanish either.
 */
export function dateFnsLocaleFor(locale: string | undefined): Locale {
  return locale?.toLowerCase().startsWith("es") ? es : enUS;
}

// `date-fns`'s default `es`-locale month token renders lowercase with a trailing period
// (e.g. "16 ene. 2026"). This produces the mockup's exact capitalized, no-period style
// ("16 Ene 2026") instead, shared by the results header's meta line and US-020's
// <RunningDaysChip> so both render dates identically.
//
// Formatted in the HOTEL's own zone, never the browser's: an instant just after midnight in
// Madrid otherwise reads as the previous day for anyone sitting west of it. `timeZone` is
// `undefined` only while the property configuration is still loading, and UTC — never the
// browser's zone — is what stands in until it arrives.
export function formatShortDate(date: Date, timeZone: string | undefined, locale: Locale): string {
  const formatted = formatInTimeZone(date, timeZone || "UTC", "d MMM yyyy", { locale });
  return formatted.replace(/^(\d+) (\p{L}+)\.? (\d+)$/u, (_match, day, month, year) => {
    return `${day} ${month.charAt(0).toUpperCase()}${month.slice(1)} ${year}`;
  });
}
